import React, { useEffect } from 'react';
import { Outlet, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAdmin } from '../../contexts/AdminContext';
import { Button } from '../../components/ui/button';
import {
  LayoutDashboard,
  Users,
  DollarSign,
  FileText,
  Settings,
  LogOut,
  Shield,
  Menu,
  X,
  Send,
  Ticket,
  UserPlus
} from 'lucide-react';
import { useState } from 'react';

const AdminLayout = () => {
  const { isAuthenticated, admin, logout } = useAdmin();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Admin UI is always in light mode regardless of any other user's saved theme
  useEffect(() => {
    document.documentElement.classList.remove('dark');
  }, []);

  if (!isAuthenticated()) {
    return <Navigate to="/admin/login" />;
  }

  const menuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/admin/dashboard', permission: null },
    { icon: Users, label: 'User Management', path: '/admin/users', permission: 'operations' },
    { icon: DollarSign, label: 'Financial', path: '/admin/financial', permission: 'finances' },
    { icon: DollarSign, label: 'Pricing Config', path: '/admin/pricing', permission: 'finances' },
    { icon: Send, label: 'Disbursements', path: '/admin/disbursements', permission: 'finances' },
    { icon: Ticket, label: 'Promotions', path: '/admin/promotions', permission: 'finances' },
    { icon: UserPlus, label: 'Referrals', path: '/admin/referrals', permission: 'finances' },
    { icon: FileText, label: 'Reports', path: '/admin/reports', permission: 'customer_support' },
    { icon: Settings, label: 'Settings', path: '/admin/settings', permission: null },
  ];

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const hasAccess = (permission) => {
    if (!permission) return true;
    return admin?.is_super_admin || admin?.roles?.[permission];
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-green-800 text-white transform transition-transform duration-200 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="p-6 border-b border-green-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Shield className="w-8 h-8" />
                <div>
                  <h1 className="text-xl font-bold">Ride Yachangu</h1>
                  <p className="text-xs text-green-300">Admin Portal</p>
                </div>
              </div>
              <button
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden text-white"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Admin Info */}
          <div className="p-4 border-b border-green-700">
            <div className="bg-green-700 rounded-lg p-3">
              <p className="text-sm font-semibold">{admin?.username}</p>
              <p className="text-xs text-green-300">
                {admin?.is_super_admin ? 'Super Admin' : 'Admin'}
              </p>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2">
            {menuItems.map((item) => {
              if (!hasAccess(item.permission)) return null;
              
              const isActive = location.pathname === item.path;
              return (
                <button
                  key={item.path}
                  onClick={() => {
                    navigate(item.path);
                    setSidebarOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-green-700 text-white'
                      : 'text-green-100 hover:bg-green-700/50'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </button>
              );
            })}
          </nav>

          {/* Logout */}
          <div className="p-4 border-t border-green-700">
            <Button
              onClick={handleLogout}
              className="w-full bg-red-600 hover:bg-red-700 text-white flex items-center gap-2"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top Bar */}
        <header className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-gray-600"
            >
              <Menu className="w-6 h-6" />
            </button>
            <div className="lg:block hidden">
              <h2 className="text-2xl font-bold text-gray-800">
                {menuItems.find((item) => item.path === location.pathname)?.label || 'Dashboard'}
              </h2>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm font-semibold text-gray-800">{admin?.username}</p>
                <p className="text-xs text-gray-500">
                  {admin?.is_super_admin ? 'Super Admin' : 'Administrator'}
                </p>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-6 overflow-auto">
          <Outlet />
        </main>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminLayout;
